import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, TextField, Button, Grid, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Divider, Alert, CircularProgress, Card, CardContent, CardHeader,
  Select, MenuItem, FormControl, InputLabel, Snackbar, Slider
} from '@mui/material';
import {
  Add, Delete, Save, ArrowBack, MedicalServices,
  Restaurant, LocalHospital, SelfImprovement
} from '@mui/icons-material';
import { ipdAPI } from '../../services/api';

const emptyMedicine = { medicineName: '', dosage: '', frequency: '', duration: '', route: '', instructions: '' };
const emptyDietInstruction = { instruction: '', category: 'do' };
const emptyTreatmentInstruction = { treatmentName: '', frequency: '', duration: '', instructions: '' };

function DischargePrescription() {
  const { admissionId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [diagnosis, setDiagnosis] = useState('');
  const [notes, setNotes] = useState('');
  const [investigations, setInvestigations] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpContact, setFollowUpContact] = useState('');
  const [improvementPercentage, setImprovementPercentage] = useState(null);
  const [improvementText, setImprovementText] = useState('');
  const [yogaSuggested, setYogaSuggested] = useState('');
  const [medicines, setMedicines] = useState([{ ...emptyMedicine }]);
  const [dietInstructions, setDietInstructions] = useState([{ ...emptyDietInstruction }]);
  const [treatmentInstructions, setTreatmentInstructions] = useState([{ ...emptyTreatmentInstruction }]);

  const [admissionInfo, setAdmissionInfo] = useState(null);

  useEffect(() => {
    loadData();
  }, [admissionId]);

  const loadData = async () => {
    try {
      // Load full admission details (includes clinical notes)
      let admissionDetails = null;
      try {
        const detailsRes = await ipdAPI.getAdmissionDetails(admissionId);
        admissionDetails = detailsRes.data.data;
        setAdmissionInfo(admissionDetails);
      } catch {
        // Fallback: find from active admissions list
        const admissionsRes = await ipdAPI.getActiveAdmissions();
        const admission = admissionsRes.data.data.find(a => a.id === parseInt(admissionId));
        if (admission) setAdmissionInfo(admission);
      }

      // Load existing prescription if any
      const res = await ipdAPI.getDischargePrescription(admissionId);
      const data = res.data.data;

      if (data) {
        // Prescription exists — load saved values
        setDiagnosis(data.prescription.diagnosis || '');
        setNotes(data.prescription.notes || '');
        setInvestigations(data.prescription.investigations || '');
        setFollowUpDate(data.prescription.follow_up_date ? data.prescription.follow_up_date.split('T')[0] : '');
        setFollowUpContact(data.prescription.follow_up_contact || '');
        setImprovementPercentage(data.prescription.improvement_percentage ?? null);
        setImprovementText(data.prescription.improvement_text || '');
        setYogaSuggested(data.prescription.yoga_suggested || '');

        if (data.medicines.length > 0) {
          setMedicines(data.medicines.map(m => ({
            medicineName: m.medicine_name,
            dosage: m.dosage || '',
            frequency: m.frequency || '',
            duration: m.duration || '',
            route: m.route || '',
            instructions: m.instructions || ''
          })));
        }

        if (data.dietInstructions.length > 0) {
          setDietInstructions(data.dietInstructions.map(d => ({
            instruction: d.instruction,
            category: d.category
          })));
        }

        if (data.treatmentInstructions.length > 0) {
          setTreatmentInstructions(data.treatmentInstructions.map(t => ({
            treatmentName: t.treatment_name,
            frequency: t.frequency || '',
            duration: t.duration || '',
            instructions: t.instructions || ''
          })));
        }
      } else if (admissionDetails) {
        // No prescription yet — prefill from clinical notes
        if (admissionDetails.diagnosis) setDiagnosis(admissionDetails.diagnosis);
        else if (admissionDetails.reason) setDiagnosis(admissionDetails.reason);
        if (admissionDetails.doctor_notes) setNotes(admissionDetails.doctor_notes);
      }
    } catch (err) {
      console.error(err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Filter out empty rows
    const validMedicines = medicines.filter(m => m.medicineName.trim());
    const validDiet = dietInstructions.filter(d => d.instruction.trim());
    const validTreatments = treatmentInstructions.filter(t => t.treatmentName.trim());

    setSaving(true);
    setError('');

    try {
      await ipdAPI.createDischargePrescription(admissionId, {
        diagnosis,
        notes,
        investigations: investigations || null,
        followUpDate: followUpDate || null,
        followUpContact: followUpContact || null,
        improvementPercentage: improvementPercentage !== null ? Number(improvementPercentage) : null,
        improvementText: improvementText || null,
        yogaSuggested: yogaSuggested || null,
        medicines: validMedicines,
        dietInstructions: validDiet,
        treatmentInstructions: validTreatments
      });
      setSuccess('Discharge prescription saved successfully');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save discharge prescription');
    } finally {
      setSaving(false);
    }
  };

  // Medicine handlers
  const addMedicine = () => setMedicines([...medicines, { ...emptyMedicine }]);
  const removeMedicine = (idx) => {
    if (medicines.length > 1) setMedicines(medicines.filter((_, i) => i !== idx));
  };
  const updateMedicine = (idx, field, value) => {
    const updated = [...medicines];
    updated[idx][field] = value;
    setMedicines(updated);
  };

  // Diet handlers
  const addDietInstruction = () => setDietInstructions([...dietInstructions, { ...emptyDietInstruction }]);
  const removeDietInstruction = (idx) => {
    if (dietInstructions.length > 1) setDietInstructions(dietInstructions.filter((_, i) => i !== idx));
  };
  const updateDietInstruction = (idx, field, value) => {
    const updated = [...dietInstructions];
    updated[idx][field] = value;
    setDietInstructions(updated);
  };

  // Treatment handlers
  const addTreatmentInstruction = () => setTreatmentInstructions([...treatmentInstructions, { ...emptyTreatmentInstruction }]);
  const removeTreatmentInstruction = (idx) => {
    if (treatmentInstructions.length > 1) setTreatmentInstructions(treatmentInstructions.filter((_, i) => i !== idx));
  };
  const updateTreatmentInstruction = (idx, field, value) => {
    const updated = [...treatmentInstructions];
    updated[idx][field] = value;
    setTreatmentInstructions(updated);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h5" fontWeight="bold">
          Discharge Prescription
        </Typography>
      </Box>

      {admissionInfo && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Typography variant="body2"><strong>Patient:</strong> {admissionInfo.first_name} {admissionInfo.last_name}</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="body2"><strong>Code:</strong> {admissionInfo.patient_code}</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="body2"><strong>Room:</strong> {admissionInfo.room_number} ({admissionInfo.category_name})</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="body2"><strong>Days Admitted:</strong> {admissionInfo.days_admitted}</Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Diagnosis & Notes */}
      <Card sx={{ mb: 3 }}>
        <CardHeader title="Diagnosis & Notes" titleTypographyProps={{ variant: 'h6' }} />
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Diagnosis"
                multiline
                rows={2}
                value={diagnosis}
                onChange={(e) => setDiagnosis(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="General Notes / Advice"
                multiline
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Investigations"
                multiline
                rows={2}
                placeholder="e.g., CBC, Blood Sugar, LFT, KFT, ECG, X-Ray Chest..."
                value={investigations}
                onChange={(e) => setInvestigations(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Follow-up Date"
                type="date"
                value={followUpDate}
                onChange={(e) => setFollowUpDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Follow-up Contact Details"
                placeholder="e.g., Call +91-XXXXXXXXXX or visit our center"
                value={followUpContact}
                onChange={(e) => setFollowUpContact(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2">
                    Discharge Improvement
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" color={
                    improvementPercentage === null ? 'text.secondary' :
                    improvementPercentage >= 75 ? 'success.main' :
                    improvementPercentage >= 40 ? 'warning.main' : 'error.main'
                  }>
                    {improvementPercentage === null ? 'Not set' : `${improvementPercentage}%`}
                  </Typography>
                </Box>
                <Slider
                  value={improvementPercentage ?? 0}
                  onChange={(_, val) => setImprovementPercentage(val)}
                  min={0}
                  max={100}
                  step={5}
                  marks={[{ value: 0, label: '0%' }, { value: 50, label: '50%' }, { value: 100, label: '100%' }]}
                  valueLabelDisplay="auto"
                  color={
                    improvementPercentage === null || improvementPercentage < 40 ? 'error' :
                    improvementPercentage < 75 ? 'warning' : 'success'
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Overall Improvement Description"
                multiline
                rows={3}
                placeholder="Describe the patient's overall improvement in detail (e.g., Pain reduced significantly, mobility improved, BP normalised...)"
                value={improvementText}
                onChange={(e) => setImprovementText(e.target.value)}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Medicines */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title={
            <Box display="flex" alignItems="center">
              <MedicalServices sx={{ mr: 1 }} />
              Medicines
            </Box>
          }
          titleTypographyProps={{ variant: 'h6' }}
          action={
            <Button startIcon={<Add />} onClick={addMedicine} size="small">
              Add Medicine
            </Button>
          }
        />
        <CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Medicine Name</strong></TableCell>
                  <TableCell><strong>Dosage</strong></TableCell>
                  <TableCell><strong>Frequency</strong></TableCell>
                  <TableCell><strong>Duration</strong></TableCell>
                  <TableCell><strong>Route</strong></TableCell>
                  <TableCell><strong>Instructions</strong></TableCell>
                  <TableCell width={50}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {medicines.map((med, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="e.g., Ashwagandha"
                        value={med.medicineName}
                        onChange={(e) => updateMedicine(idx, 'medicineName', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="e.g., 500mg"
                        value={med.dosage}
                        onChange={(e) => updateMedicine(idx, 'dosage', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="e.g., Twice daily"
                        value={med.frequency}
                        onChange={(e) => updateMedicine(idx, 'frequency', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="e.g., 15 days"
                        value={med.duration}
                        onChange={(e) => updateMedicine(idx, 'duration', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="e.g., Oral"
                        value={med.route}
                        onChange={(e) => updateMedicine(idx, 'route', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="e.g., After meals"
                        value={med.instructions}
                        onChange={(e) => updateMedicine(idx, 'instructions', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" color="error" onClick={() => removeMedicine(idx)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Diet Instructions */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title={
            <Box display="flex" alignItems="center">
              <Restaurant sx={{ mr: 1 }} />
              Diet Instructions
            </Box>
          }
          titleTypographyProps={{ variant: 'h6' }}
          action={
            <Button startIcon={<Add />} onClick={addDietInstruction} size="small">
              Add Instruction
            </Button>
          }
        />
        <CardContent>
          {dietInstructions.map((diet, idx) => (
            <Box key={idx} display="flex" alignItems="center" gap={2} mb={1}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={diet.category}
                  label="Category"
                  onChange={(e) => updateDietInstruction(idx, 'category', e.target.value)}
                >
                  <MenuItem value="do">Do's</MenuItem>
                  <MenuItem value="dont">Don'ts</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                fullWidth
                placeholder="e.g., Eat fresh fruits and vegetables"
                value={diet.instruction}
                onChange={(e) => updateDietInstruction(idx, 'instruction', e.target.value)}
              />
              <IconButton size="small" color="error" onClick={() => removeDietInstruction(idx)}>
                <Delete fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </CardContent>
      </Card>

      {/* Treatment Instructions */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title={
            <Box display="flex" alignItems="center">
              <LocalHospital sx={{ mr: 1 }} />
              Treatment Instructions (Post-Discharge)
            </Box>
          }
          titleTypographyProps={{ variant: 'h6' }}
          action={
            <Button startIcon={<Add />} onClick={addTreatmentInstruction} size="small">
              Add Treatment
            </Button>
          }
        />
        <CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Treatment Name</strong></TableCell>
                  <TableCell><strong>Frequency</strong></TableCell>
                  <TableCell><strong>Duration</strong></TableCell>
                  <TableCell><strong>Instructions</strong></TableCell>
                  <TableCell width={50}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {treatmentInstructions.map((t, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="e.g., Steam Bath"
                        value={t.treatmentName}
                        onChange={(e) => updateTreatmentInstruction(idx, 'treatmentName', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="e.g., Weekly"
                        value={t.frequency}
                        onChange={(e) => updateTreatmentInstruction(idx, 'frequency', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="e.g., 1 month"
                        value={t.duration}
                        onChange={(e) => updateTreatmentInstruction(idx, 'duration', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="e.g., At home with warm water"
                        value={t.instructions}
                        onChange={(e) => updateTreatmentInstruction(idx, 'instructions', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" color="error" onClick={() => removeTreatmentInstruction(idx)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Yoga / Exercise Suggestions */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title={
            <Box display="flex" alignItems="center">
              <SelfImprovement sx={{ mr: 1 }} />
              Yoga / Exercise Suggestions
            </Box>
          }
          titleTypographyProps={{ variant: 'h6' }}
        />
        <CardContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Yoga & Exercise Recommendations"
            placeholder="e.g., Pranayama – Anulom Vilom 10 min daily&#10;Surya Namaskar – 12 rounds&#10;Meditation – 20 min morning"
            value={yogaSuggested}
            onChange={(e) => setYogaSuggested(e.target.value)}
          />
        </CardContent>
      </Card>

      <Divider sx={{ mb: 3 }} />

      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button variant="outlined" onClick={() => navigate(-1)}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Prescription'}
        </Button>
      </Box>

      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess('')}
        message={success}
      />
    </Box>
  );
}

export default DischargePrescription;
